import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import CustomDropdown, { DropdownOption } from '../../components/ui/CustomDropdown';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { Questionnaire } from '../../types/questionnaire';
import { QuestionnaireService } from '../../services/questionnaireService';
import { ContentLanguageMetadataService } from '../../services/contentLanguageMetadataService';
import '../../styles/shared/dashboard.css';
import '../../styles/pages/QuestionnairePages.css';
import '../../styles/pages/QuestionnaireHubModern.css';
import '../../styles/pages/CompactQuestionnaireHeader.css';

type PublisherTab = 'open' | 'completed';

const QuestionnairePublisherPage: React.FC = () => {
  const [rawMyQuestionnaires, setRawMyQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'points' | 'category'>('newest');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<PublisherTab>('open');
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null);
  // Questionnaire detail panel state
  const [showQuestionnaireDetail, setShowQuestionnaireDetail] = useState(false);

  // Responsive state
  const [showMobileView, setShowMobileView] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [questionnairesPerPage] = useState(10);

  const [stats, setStats] = useState({
    open: 0,
    completed: 0
  });

  const { user } = useAuth();
  const { t } = useLanguage();
  const { deviceType } = useDeviceType();

  // Dropdown options
  const categoryOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: t('questionnaires.allSkills') },
    { value: 'Programming', label: t('questionnaires.programming') },
    { value: 'Design', label: t('questionnaires.design') },
    { value: 'Marketing', label: t('questionnaires.marketing') },
    { value: 'Research', label: t('questionnaires.research') }
  ], [t]);

  const sortOptions: DropdownOption[] = useMemo(() => [
    { value: 'newest', label: t('questionnaires.newestFirst') },
    { value: 'points', label: t('questionnaires.highestPoints') },
    { value: 'category', label: t('questionnaires.category') }
  ], [t]);

  // Set responsive states based on device type
  useEffect(() => {
    if (!deviceType) return;

    const isMobile = deviceType === DeviceType.MOBILE;
    const isTablet = deviceType === DeviceType.TABLET;

    setIsCompactView(isMobile || isTablet);
    setShowMobileView(isMobile || isTablet);
  }, [deviceType]);

  // Scroll effect for compact header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.compact-questionnaire-header');
      if (header) {
        if (window.scrollY > 10) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Publisher Type Buttons Configuration
  const publisherTypeButtons = useMemo(() => [
    {
      id: 'open' as PublisherTab,
      label: t('questionnaires.open'),
      hint: t('questionnaires.hintOpen'),
      icon: 'bi-unlock',
      count: stats.open,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'completed' as PublisherTab,
      label: t('questionnaires.completed'),
      hint: t('questionnaires.hintOpenCompleted'),
      icon: 'bi-check-circle',
      count: stats.completed,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    }
  ], [t, stats.open, stats.completed]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to refetch and update questionnaire data
  const refetchQuestionnaireData = useCallback(async () => {
    if (!user) return;

    try {
      // Fetch user's created questionnaires
      const myQuestionnairesResult = await QuestionnaireService.getMyPublishedQuestionnaires();

      if (myQuestionnairesResult.error) throw myQuestionnairesResult.error;

      const myQuestionnairesData = myQuestionnairesResult.questionnaires || [];

      console.log('📋 Publisher questionnaires data:', myQuestionnairesData);

      // Calculate stats
      const openQuestionnairesCount = myQuestionnairesData.filter(q => q.status === 'published' || q.status === 'open').length;
      const completedQuestionnairesCount = myQuestionnairesData.filter(q => q.status === 'filled' || q.status === 'closed').length;

      // Update state
      setRawMyQuestionnaires(myQuestionnairesData);

      // Calculate stats
      setStats({
        open: openQuestionnairesCount,
        completed: completedQuestionnairesCount
      });

      // Extract categories from questionnaires
      const categorySet = new Set<string>();
      myQuestionnairesData.forEach(questionnaire => {
        if (questionnaire.skill_required) categorySet.add(questionnaire.skill_required);
      });
      setCategories(Array.from(categorySet));

      console.log('📊 Publisher stats:', {
        open: openQuestionnairesCount,
        completed: completedQuestionnairesCount
      });

    } catch (err) {
      console.error('Error fetching questionnaires:', err);
      setError('Failed to load questionnaires. Please try again later.');
    }
  }, [user]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchQuestionnaires = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Use the refetchQuestionnaireData function for initial load
        await refetchQuestionnaireData();

      } catch (err) {
        console.error('Error fetching questionnaires:', err);
        setError('Failed to load questionnaires. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch on user changes
    fetchQuestionnaires();
  }, [user?.id, refetchQuestionnaireData]);

  // Sort function
  const sortQuestionnaires = useCallback((questionnaires: Questionnaire[]) => {
    const sortedQuestionnaires = [...questionnaires];
    
    switch (sortBy) {
      case 'newest':
        return sortedQuestionnaires.sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
      case 'points':
        return sortedQuestionnaires.sort((a, b) => (b.reward_points || 0) - (a.reward_points || 0));
      case 'category':
        return sortedQuestionnaires.sort((a, b) => (a.skill_required || '').localeCompare(b.skill_required || ''));
      default:
        return sortedQuestionnaires;
    }
  }, [sortBy]);

  // Filter questionnaires based on search and filters
  const filteredMyQuestionnaires = useMemo(() => {
    const filtered = rawMyQuestionnaires.filter(questionnaire => {
      const matchesSearch = !debouncedSearchTerm ||
        questionnaire.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (questionnaire.description && questionnaire.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

      const matchesCategory = categoryFilter === 'all' || questionnaire.skill_required === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    // Always include the selected questionnaire even if it doesn't match filters
    if (selectedQuestionnaire && !filtered.find(q => q.id === selectedQuestionnaire.id)) {
      const selectedQuestionnaireInRaw = rawMyQuestionnaires.find(q => q.id === selectedQuestionnaire.id);
      if (selectedQuestionnaireInRaw) {
        filtered.push(selectedQuestionnaireInRaw);
      }
    }

    return sortQuestionnaires(filtered);
  }, [rawMyQuestionnaires, debouncedSearchTerm, categoryFilter, selectedQuestionnaire, sortQuestionnaires]);

  // Get current questionnaires based on active tab
  const getCurrentQuestionnaires = useMemo(() => {
    let filtered = filteredMyQuestionnaires;

    // Apply tab filter
    if (activeTab === 'open') {
      filtered = filtered.filter(questionnaire => questionnaire.status === 'published' || questionnaire.status === 'open');
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(questionnaire => questionnaire.status === 'filled' || questionnaire.status === 'closed');
    }

    return filtered;
  }, [activeTab, filteredMyQuestionnaires]);

  // Pagination
  const totalPages = Math.ceil(getCurrentQuestionnaires.length / questionnairesPerPage);
  const currentQuestionnaires = getCurrentQuestionnaires.slice(
    (currentPage - 1) * questionnairesPerPage,
    currentPage * questionnairesPerPage
  );

  const handleTabSwitch = useCallback((tab: PublisherTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedQuestionnaire(null);
    setShowQuestionnaireDetail(false);
  }, []);

  const handleQuestionnaireSelect = useCallback((questionnaire: Questionnaire) => {
    // Keep the questionnaire visible in the list when selected
    setSelectedQuestionnaire(prevSelected => prevSelected?.id === questionnaire.id ? prevSelected : questionnaire);
    setShowQuestionnaireDetail(true);
  }, []);

  // Helper function to get language display name
  const getLanguageDisplay = (lang: string | null): string => {
    if (!lang) return 'English'; // Default to English
    return lang === 'zh' ? '中文' : 'English';
  };

  // Preserve selected questionnaire after data refresh by finding it in the updated lists
  useEffect(() => {
    if (selectedQuestionnaire) {
      // Find the selected questionnaire in the updated data
      const updatedQuestionnaire = rawMyQuestionnaires.find(
        q => q.id === selectedQuestionnaire.id
      );
      
      // Update selectedQuestionnaire with fresh data if found, otherwise keep the current one
      if (updatedQuestionnaire && JSON.stringify(updatedQuestionnaire) !== JSON.stringify(selectedQuestionnaire)) {
        setSelectedQuestionnaire(updatedQuestionnaire);
      }
    }
  }, [rawMyQuestionnaires, selectedQuestionnaire]);

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="loading-container">
          <div className="loading-ring"></div>
          <p>{t('questionnaires.loadingQuestionnaires')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (error) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="error-container">
          <h3>{t('common.error')}</h3>
          <p>{error}</p>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="questionnaire-hub-container">
        {/* Compact Header */}
        <div className="compact-questionnaire-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('questionnaires.publisherDashboard')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.questionnaires')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('navigation.publisher')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.open}</span>
                  <span className="stat-label-mini">{t('questionnaires.open')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">
                    {rawMyQuestionnaires.reduce((total, q) => total + (q.reward_points || 0), 0)}
                  </span>
                  <span className="stat-label-mini">{t('questionnaires.totalPoints')}</span>
                </div>
              </div>
              <Link to="/create-questionnaire" className="compact-action-button compact-tooltip" data-tooltip={t('questionnaires.createQuestionnaire')}>
                <i className="bi bi-plus-circle"></i>
                {t('questionnaires.createQuestionnaire')}
              </Link>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Publisher Type Buttons */}
              <div className="compact-type-buttons">
                {publisherTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              <div className="compact-search-filters">
                <div className="compact-search-bar">
                  <i className="bi bi-search compact-search-icon"></i>
                  <input
                    type="text"
                    className="compact-search-input"
                    placeholder={t('questionnaires.searchQuestionnaires')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={categoryOptions}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    placeholder={t('questionnaires.category')}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`questionnaire-hub-main-two-column ${showQuestionnaireDetail ? 'detail-panel-open' : ''}`}>
          {/* Questionnaire List */}
          <div className="questionnaire-hub-questionnaire-list-column">
            <div className="questionnaire-hub-questionnaire-list-header">
              <span className="questionnaire-hub-results-count">
                {getCurrentQuestionnaires.length === 1 
                  ? t('questionnaires.resultsCountSingular', { count: getCurrentQuestionnaires.length })
                  : t('questionnaires.resultsCount', { count: getCurrentQuestionnaires.length })
                }
              </span>
              <div className="questionnaire-hub-sort-controls">
                <button
                  onClick={refetchQuestionnaireData}
                  className="questionnaire-hub-refresh-button"
                  title={t('questionnaires.refresh')}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                </button>
                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={sortOptions}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as 'newest' | 'points' | 'category')}
                    placeholder={t('questionnaires.sortBy')}
                    size="small"
                  />
                </div>
              </div>
            </div>

            <div className="questionnaire-hub-questionnaire-list-content">
              {/* Questionnaire Cards */}
              <div className="questionnaire-hub-questionnaire-cards">
                {currentQuestionnaires.length === 0 ? (
                  <div className="no-questionnaires-message">
                    <i className="bi bi-inbox"></i>
                    <h3>{t('questionnaires.noQuestionnairesFound')}</h3>
                    <p>
                      {activeTab === 'open'
                        ? t('questionnaires.noOpenQuestionnaires')
                        : t('questionnaires.noCompletedQuestionnaires')
                      }
                    </p>
                    {activeTab === 'open' && (
                      <Link to="/create-questionnaire" className="btn btn-primary">
                        <i className="bi bi-plus-circle"></i>
                        {t('questionnaires.createFirstQuestionnaire')}
                      </Link>
                    )}
                  </div>
                ) : (
                  currentQuestionnaires.map(questionnaire => (
                    <div
                      key={questionnaire.id}
                      className={`questionnaire-card-wrapper ${selectedQuestionnaire?.id === questionnaire.id ? 'selected' : ''}`}
                      onClick={() => handleQuestionnaireSelect(questionnaire)}
                    >
                      <div className={`questionnaire-card questionnaire-card-modern publisher-questionnaire-card ${questionnaire.status?.toLowerCase()}`}>
                        <div className="questionnaire-header-row">
                          <h3 className="questionnaire-title">{questionnaire.title}</h3>
                          <div className="questionnaire-badges-container">
                            <span className="questionnaire-points">
                              <i className="bi bi-star-fill"></i>
                              {questionnaire.reward_points || 0}
                            </span>
                            <span className={`questionnaire-status-badge ${questionnaire.status?.toLowerCase()}`}>
                              <i className={`bi ${questionnaire.status === 'published' ? 'bi-megaphone' :
                                questionnaire.status === 'open' ? 'bi-unlock' :
                                questionnaire.status === 'filled' || questionnaire.status === 'closed' ? 'bi-check-circle-fill' :
                                  questionnaire.status === 'draft' ? 'bi-pencil' : 'bi-clock-history'
                                }`}></i>
                              {questionnaire.status}
                            </span>
                          </div>
                        </div>

         
                        <div className="questionnaire-meta">
                          <div className="questionnaire-meta-left">
                            <span className="questionnaire-category">
                              <i className="bi bi-tag"></i>
                              {questionnaire.skill_required || 'General'}
                            </span>
                            <span className="questionnaire-verification-info">
                              <i className="bi bi-list-ul"></i>
                              {questionnaire.total_questions || 0} questions
                            </span>
                            <span className="questionnaire-verification-info">
                              <i className="bi bi-people"></i>
                              {questionnaire.current_participants || 0}
                              {questionnaire.max_participants ? `/${questionnaire.max_participants}` : ''} responses
                            </span>
                            <span className="questionnaire-verification-info" title="Content Language">
                              <i className="bi bi-translate"></i>
                              {getLanguageDisplay(ContentLanguageMetadataService.getContentLanguage('survey', questionnaire.id))}
                            </span>
                          </div>
                          <div className="questionnaire-meta-right">
                            <span className="questionnaire-date">
                              {new Date(questionnaire.created_at || '').toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Questionnaire Detail Overlay */}
          {showQuestionnaireDetail && (
            <div className="questionnaire-detail-overlay" onClick={() => setShowQuestionnaireDetail(false)} />
          )}

          {/* Questionnaire Detail Panel - slides from right */}
          <div className={`questionnaire-detail-panel ${showQuestionnaireDetail ? 'panel-open' : ''}`}>
            {selectedQuestionnaire ? (
              <div className="questionnaire-detail-content">
                <div className="questionnaire-detail-header">
                  <div className="questionnaire-detail-header-main">
                    <div className="questionnaire-detail-title-area">
                      <h1 className="questionnaire-detail-title">{selectedQuestionnaire.title}</h1>
                      <div className="questionnaire-detail-metadata">
                        <span className="questionnaire-detail-category-badge">
                          <i className="bi bi-tag-fill"></i>
                          {selectedQuestionnaire.skill_required || 'General'}
                        </span>
                        <span className="questionnaire-detail-points-badge">
                          <i className="bi bi-star-fill"></i>
                          {selectedQuestionnaire.reward_points || 0} pts
                        </span>
                        <span className="questionnaire-detail-questions-badge">
                          <i className="bi bi-list-ul"></i>
                          {selectedQuestionnaire.total_questions || 0} questions
                        </span>
                        <span className="questionnaire-detail-participants-badge">
                          <i className="bi bi-people-fill"></i>
                          {selectedQuestionnaire.current_participants || 0}
                          {selectedQuestionnaire.max_participants ? `/${selectedQuestionnaire.max_participants}` : ''} responses
                        </span>
                        <span className={`questionnaire-detail-status-badge status-${selectedQuestionnaire.status?.toLowerCase()}`}>
                          <i className={`bi ${selectedQuestionnaire.status === 'published' ? 'bi-megaphone' :
                            selectedQuestionnaire.status === 'open' ? 'bi-unlock' :
                            selectedQuestionnaire.status === 'filled' || selectedQuestionnaire.status === 'closed' ? 'bi-check-circle-fill' :
                              selectedQuestionnaire.status === 'draft' ? 'bi-pencil' : 'bi-clock-history'
                            }`}></i>
                          {selectedQuestionnaire.status}
                        </span>
                        <span className="questionnaire-detail-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {new Date(selectedQuestionnaire.created_at || '').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="questionnaire-detail-header-actions">
                      <button 
                        className="questionnaire-detail-action-btn new-tab"
                        onClick={() => {
                          const questionnaireUrl = `/questionnaires/${selectedQuestionnaire.id}/results`;
                          window.open(questionnaireUrl, '_blank');
                        }}
                        title="Open results in new tab"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                      
                      <button 
                        className="close-questionnaire-detail-btn"
                        onClick={() => setShowQuestionnaireDetail(false)}
                        title="Close"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="questionnaire-detail-content">
                  <div style={{ padding: '2rem', flex: 1, overflowY: 'auto' }}>
                    {/* Action buttons section */}
                    <section className="questionnaire-details-section">
                      <div className="questionnaire-details-section-header">
                        <h2 className="questionnaire-details-section-title">
                          <i className="bi bi-play-circle"></i>
                          {t('questionnaires.actions')}
                        </h2>
                      </div>
                      <div className="questionnaire-details-section-content">
                        <div className="questionnaire-details-actions-grid">
                          <Link
                            to={`/questionnaires/${selectedQuestionnaire.id}/results`}
                            className="questionnaire-hub-action-button primary"
                          >
                            <i className="bi bi-graph-up"></i>
                            {t('questionnaires.viewResults')}
                          </Link>
                          <Link
                            to={`/questionnaires/${selectedQuestionnaire.id}/edit`}
                            className="questionnaire-hub-action-button secondary"
                          >
                            <i className="bi bi-pencil"></i>
                            {t('questionnaires.edit')}
                          </Link>
                        </div>
                      </div>
                    </section>

                    {/* Description */}
                    <section className="questionnaire-details-section">
                      <div className="questionnaire-details-section-header">
                        <h2 className="questionnaire-details-section-title">
                          <i className="bi bi-file-text"></i>
                          {t('questionnaires.description')}
                        </h2>
                      </div>
                      <div className="questionnaire-details-section-content">
                        <p className="questionnaire-details-description">
                          {selectedQuestionnaire.description || t('questionnaires.noDescriptionAvailable')}
                        </p>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

        </div>
      </div>
    </BasePageLayout>
  );
};

export default QuestionnairePublisherPage;
